import * as React from "react";
import Modal from "@mui/material/Modal";
import { Autocomplete, Box, Button, CircularProgress, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Form, Formik } from "formik";
import {
  StyledBox,
  StyledBoxCard,
  StyledMain,
  StyledTypographyOrder,
} from "../modals/create-order/style";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import CrossIcon from "../../assets/Icons/CrossIcon";
import { StyledTypography } from "../modals/manual-split-calculation/style";
import { StyledBoxFlex } from "../modals/success-virtual-locker/style";
import { VIRTUAL_LOCKER_GET_ITEM } from "../../actions/VirtualLockerAction";
import theme from "../../theme";
import {
  GROUP_CART_ADD_ITEMS,
  GROUP_CART_LIST,
  GROUP_CART_SINGLE,
} from "../../actions/group-cart-actions";
import toast from "react-hot-toast";

// Yup Validation Schema
const validationSchema = Yup.object({
  selectedItems: Yup.array()
    .min(1, "Please select at least one product")
    .required("Please select at least one product"),
});

export default function UnassignedItems({ open, handleClose, cartId }) {
  const dispatch = useDispatch();
  const submitLock = React.useRef(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isCartLoading = useSelector((state) => state?.groupCartData?.isMeLoading);
  const isSubmitDisabled = isSubmitting || isCartLoading;
  const products = useSelector((state) => state?.virtual?.lockerGetList?.data);
  const singleCart = useSelector((state) => state?.groupCartData?.singleCart);
  const availableItems = products?.map((item) => {
    return {
      label: item?.product_name,
      value: item?.id,
    };
  });

  const getItemLocker = React.useCallback(
    (values) => {
      dispatch({
        type: VIRTUAL_LOCKER_GET_ITEM,
        payload: values,
      });
    },
    [dispatch]
  );

  React.useEffect(() => {
    if (!open) {
      submitLock.current = false;
      setIsSubmitting(false);
      return;
    }
    getItemLocker({ status: "pending" });
  }, [getItemLocker, open]);

  const handleSubmit = (values) => {
    if (submitLock.current || isSubmitDisabled) return;

    const selectedIds = values.selectedItems.map((item) => item.value);

    const alreadyAddedIds = singleCart?.itemMappings?.map((m) => m?.item?.id) || [];
    const duplicates = values.selectedItems.filter((item) =>
      alreadyAddedIds.includes(item.value)
    );

    if (duplicates.length > 0) {
      const names = duplicates.map((d) => d.label).join(", ");
      toast.error(`Already added to cart: ${names}`);
      return;
    }

    submitLock.current = true;
    setIsSubmitting(true);

    const releaseSubmit = () => {
      submitLock.current = false;
      setIsSubmitting(false);
    };

    dispatch({
      type: GROUP_CART_ADD_ITEMS,
      payload: {
        groupCartId: cartId,
        itemIds: selectedIds,
      },
      onSuccess: () => {
        dispatch({
          type: GROUP_CART_SINGLE,
          payload: { id: cartId },
        });
        dispatch({
          type: GROUP_CART_LIST,
          payload: { page: 1, limit: 10 },
        });
        handleClose();
      },
      onError: releaseSubmit,
    });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <StyledBox>
        <StyledBoxCard>
          <StyledBoxFlex>
            <StyledTypography>Create Items</StyledTypography>
          </StyledBoxFlex>
          <CrossIcon color={theme.palette.custom.white} onClick={handleClose} />
        </StyledBoxCard>

        <Formik
          initialValues={{
            selectedItems: [],
          }}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Box>
                <StyledMain>
                  <Grid container spacing={2}>
                    {/* Multi Select Autocomplete */}
                    <Grid
                      size={{
                        lg: 12,
                        md: 12,
                        sm: 12,
                        xs: 12,
                      }}
                    >
                      <StyledTypographyOrder>
                        {" "}
                        Select Product{" "}
                      </StyledTypographyOrder>
                      <Autocomplete
                        multiple
                        options={availableItems}
                        getOptionLabel={(option) => option.label}
                        value={values.selectedItems}
                        onChange={(e, newValue) =>
                          setFieldValue("selectedItems", newValue)
                        }
                        limitTags={2} // shows 2 chips max, rest as +X
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Product"
                            error={
                              touched.selectedItems &&
                              Boolean(errors.selectedItems)
                            }
                            helperText={
                              touched.selectedItems && errors.selectedItems
                            }
                          />
                        )}
                      />
                    </Grid>

                    {/* Submit Button */}
                    <Grid
                      size={{
                        lg: 12,
                        md: 12,
                        sm: 12,
                        xs: 12,
                      }}
                    >
                      <Button
                        variant="contained"
                        fullWidth
                        type="submit"
                        disabled={isSubmitDisabled}
                      >
                        {isSubmitDisabled ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Add Items"
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </StyledMain>
              </Box>
            </Form>
          )}
        </Formik>
      </StyledBox>
    </Modal>
  );
}
